import React, { useState, useEffect, Fragment, useMemo } from 'react'

import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Checkbox } from '@material-ui/core'

import { updatePet } from '../../../actions/pets/pets.actions'
import { updateMealAddOns, updateVisit } from '../../../actions/visits/visits.actions'

import { useDispatch } from 'react-redux'
import { isEqual } from 'lodash'

import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import StyledAlert from '../../Common/StyledAlert'

const useStyles = makeStyles((theme) => ({
	dialogActions: {
		justifyContent: 'flex-start'
	},
	checkbox: {
		'&.Mui-disabled.Mui-checked .MuiIconButton-label .MuiSvgIcon-root': {
			fill: '#A2A1A4'
		}
	},
	avatar: {
		background: '#C4C4C4',
		height: '32px',
		width: '32px',
		marginTop: '12px',
		marginBottom: '12px'
	},
	errorAlert: {
		marginBottom: 16
	}
}))

const EMPTY_SCHEDULE_ERROR = 'Select at least one meal time. Dogs must have at least one meal each day.'
const SKIP_ONLY_MEAL_ERROR =
	'Cannot skip the first meal when only one meal is scheduled. Add another meal time or uncheck Skip first meal.'

const EditFoodDialog = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const [selectedTimes, setSelectedTimes] = useState([])
	const [initialTimes, setInitialTimes] = useState([])
	const [skipFirstMeal, setSkipFirstMeal] = useState(false)
	const [isInitialMealSkipped, setIsInitialMealSkipped] = useState(false)

	let { isShown, setIsShown, visit, pet } = props

	let timeInterval = ['morning', 'midday', 'evening']

	// Boarding persists pet.food_schedule and requires ≥1 meal; daycare meal addons may be empty
	const requireAtLeastOneMeal = visit?.type === 'boarding'
	const hasNoMealsSelected = requireAtLeastOneMeal && selectedTimes.length === 0
	const wouldSkipOnlyMeal = requireAtLeastOneMeal && skipFirstMeal && selectedTimes.length === 1
	const scheduleUnchanged = useMemo(
		() => isEqual([...(initialTimes || [])].sort(), [...(selectedTimes || [])].sort()),
		[initialTimes, selectedTimes]
	)
	const saveDisabled =
		hasNoMealsSelected || wouldSkipOnlyMeal || (!skipFirstMeal && scheduleUnchanged)

	useEffect(() => {
		if (!isShown || !visit || !pet) return

		const mealAddOns =
			visit.add_on_services && visit.add_on_services.length
				? visit.add_on_services.filter((addOn) => addOn.category === 'meal' && addOn.pet_id == pet._id)
				: []

		const schedule =
			visit.type === 'boarding'
				? [...(pet.food_schedule || [])]
				: mealAddOns.map((m) => m.time)

		setInitialTimes(schedule)
		setSelectedTimes(schedule)
		setSkipFirstMeal(false)
		setIsInitialMealSkipped(mealAddOns.some((m) => m.skipped))
	}, [visit, pet, isShown])

	const closeDialog = () => {
		const schedule = [...(initialTimes || [])]
		setSelectedTimes(schedule)
		setSkipFirstMeal(false)
		setIsShown(false)
	}

	const saveFoodSchedule = () => {
		if (requireAtLeastOneMeal && selectedTimes.length === 0) {
			return
		}
		if (requireAtLeastOneMeal && skipFirstMeal && selectedTimes.length === 1) {
			return
		}

		//remove completed meals from selectedTimes
		let mealAddOns = visit.add_on_services
			? visit.add_on_services.filter((addOn) => addOn.category === 'meal' && addOn.pet_id == pet._id)
			: []

		let updatedTimes = selectedTimes.filter(
			(time) =>
				!mealAddOns
					.filter((addOn) => addOn.status == 'delivered' || addOn.status == 'prepared')
					.map((a) => a.time)
					.includes(time)
		)

		let updates = {
			visit_id: visit._id,
			pet_id: pet._id,
			mealTimes: updatedTimes
		}

		let petUpdates = {
			food_schedule: selectedTimes
		}

		let boardingUpdates = {
			visit_id: visit._id,
			updates: {}
		}

		if (skipFirstMeal) {
			boardingUpdates.updates.mealToSkip = {
				pet_id: pet._id,
				food_schedule: selectedTimes,
				date: visit.dates && visit.dates.length ? visit.dates[0] : moment().format('YYYY-MM-DD'),
				time:
					updatedTimes && updatedTimes.length ? updatedTimes.sort()[updatedTimes.length - 1] : 'morning'
			}
		}

		if (visit.type === 'boarding') {
			if (skipFirstMeal) dispatch(updateVisit({ ...boardingUpdates }))
			else dispatch(updatePet(pet._id, { ...petUpdates }))
		} else dispatch(updateMealAddOns({ ...updates }))
	}

	const renderTimeSelections = () => {
		return timeInterval.map((time) => {
			let index = timeInterval.findIndex((p) => p === time)
			let mealAddOns = visit.add_on_services
				? visit.add_on_services.filter((addOn) => addOn.category === 'meal' && addOn.pet_id == pet._id)
				: []

			let checked = selectedTimes.includes(time) ? true : false
			let disabled =
				mealAddOns
					.filter((addOn) => addOn.status == 'delivered' || addOn.status == 'prepared')
					.map((a) => a.time)
					.includes(time) && visit.type !== 'boarding'

			return (
				<div
					key={time}
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						borderBottom: `${index === timeInterval.length - 1 ? 0 : 1}px solid #D5D5D7`,
						padding: '16px'
					}}>
					<Checkbox
						checked={checked}
						disabled={disabled}
						className={classes.checkbox}
						onChange={(e) => {
							if (selectedTimes.includes(time)) {
								let updatedSelectedTimes = selectedTimes.filter((t) => t !== time)
								setSelectedTimes(updatedSelectedTimes)
							} else {
								let updatedSelectedTimes = [...selectedTimes, time]
								setSelectedTimes(updatedSelectedTimes)
							}
						}}
						style={{
							marginLeft: '8px',
							marginRight: '8px'
						}}
					/>
					<Typography style={{ marginLeft: '16px', textTransform: 'capitalize' }}>{time}</Typography>
				</div>
			)
		})
	}

	const renderSkipFirstMeal = () => (
		<div
			style={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				padding: '16px'
			}}>
			<Checkbox
				checked={skipFirstMeal || isInitialMealSkipped ? true : false}
				className={classes.checkbox}
				disabled={isInitialMealSkipped}
				onChange={(e) => setSkipFirstMeal(!skipFirstMeal)}
				style={{
					marginLeft: '8px',
					marginRight: '8px'
				}}
			/>
			<Typography style={{ marginLeft: '16px', textTransform: 'capitalize' }}>Skip first meal</Typography>
		</div>
	)

	return (
		<Dialog
			open={isShown}
			className={classes.dialog}
			onClose={closeDialog}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle
				style={{
					marginBottom: 16
				}}>
				EDIT FOOD
			</DialogTitle>
			<DialogContent>
				<Typography
					variant="body2"
					style={{
						marginBottom: 24
					}}>
					Choose the times this dog receives their daily meals
				</Typography>

				<Typography
					variant="overline"
					style={{
						marginBottom: 8
					}}>
					Timeblock
				</Typography>

				<div
					style={{
						border: '1px solid #D5D5D7',
						borderRadius: '6px',
						marginBottom: '16px'
					}}>
					{renderTimeSelections()}
				</div>
				{hasNoMealsSelected ? (
					<div className={classes.errorAlert}>
						<StyledAlert type="error" text={EMPTY_SCHEDULE_ERROR} />
					</div>
				) : null}
				{wouldSkipOnlyMeal ? (
					<div className={classes.errorAlert}>
						<StyledAlert type="error" text={SKIP_ONLY_MEAL_ERROR} />
					</div>
				) : null}
				{visit.type === 'boarding' ? (
					<Fragment>
						<Typography
							variant="overline"
							style={{
								marginBottom: 8
							}}>
							Exceptions
						</Typography>

						<div
							style={{
								border: '1px solid #D5D5D7',
								borderRadius: '6px',
								marginBottom: '16px'
							}}>
							{renderSkipFirstMeal()}
						</div>
					</Fragment>
				) : null}
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					variant="contained"
					disabled={saveDisabled}
					onClick={() => {
						saveFoodSchedule()
						setIsShown(false)
					}}
					color="secondary">
					Save
				</Button>

				<Button variant="outlined" onClick={closeDialog} color="secondary">
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default EditFoodDialog
